import { useState, useRef, useCallback, useEffect } from 'react';
import { getStateMachineStatus } from '../../utilities/mezzanineApi';

const POLL_INTERVAL = 5000; // 5 seconds

/**
 * Hook to track state machine executions for channels.
 * Used by both manual Deploy button and auto_deploy from create form.
 * 
 * @param {Function} onComplete - Callback when any execution completes (success or fail)
 * @returns {Object} - { executions, startTracking, isExecuting }
 */
const useExecutionTracker = (onComplete) => {
  // Map of channelId -> { action, status, executionArn }
  const [executions, setExecutions] = useState({});
  const pollingIntervals = useRef({});

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
    };
  }, []);

  // Stop tracking a channel
  const stopTracking = useCallback((channelId) => {
    if (pollingIntervals.current[channelId]) {
      clearInterval(pollingIntervals.current[channelId]);
      delete pollingIntervals.current[channelId];
    }
    setExecutions(prev => {
      const updated = { ...prev };
      delete updated[channelId];
      return updated;
    });
  }, []);

  // Poll execution status
  const pollStatus = useCallback(async (channelId, executionArn, action) => {
    try {
      const status = await getStateMachineStatus(executionArn);
      
      if (status.status === 'SUCCEEDED') {
        stopTracking(channelId);
        if (onComplete) {
          onComplete({ channelId, action, success: true, status: status.status });
        }
      } else if (status.status === 'FAILED' || status.status === 'TIMED_OUT' || status.status === 'ABORTED') {
        stopTracking(channelId);
        if (onComplete) {
          onComplete({ 
            channelId, 
            action, 
            success: false, 
            status: status.status,
            error: status.error || status.cause || status.status 
          });
        }
      }
      // If RUNNING, continue polling
    } catch (err) {
      console.error('Error polling execution status:', err);
    }
  }, [stopTracking, onComplete]);

  // Start tracking an execution
  const startTracking = useCallback((channelId, executionArn, action = 'deploy') => {
    // Don't track if already tracking this channel
    if (pollingIntervals.current[channelId]) {
      return;
    }

    // Update state
    setExecutions(prev => ({
      ...prev,
      [channelId]: { action, status: 'RUNNING', executionArn }
    }));

    // Start polling
    pollingIntervals.current[channelId] = setInterval(
      () => pollStatus(channelId, executionArn, action),
      POLL_INTERVAL
    );

    // Poll immediately
    pollStatus(channelId, executionArn, action);
  }, [pollStatus]);

  // Check if a channel has an execution running
  const isExecuting = useCallback((channelId) => {
    return !!executions[channelId];
  }, [executions]);

  // Get execution info for a channel
  const getExecution = useCallback((channelId) => {
    return executions[channelId] || null;
  }, [executions]);

  return {
    executions,
    startTracking,
    stopTracking,
    isExecuting,
    getExecution
  };
};

export default useExecutionTracker;
